// src/pages/MediaKitBuilderPage.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Plus, Trash2, Link as LinkIcon, Loader2, Check, X, Eye, EyeOff,
    Save, ExternalLink, Palette, Layout, GripVertical,
} from 'lucide-react';
import { mediaKitApi } from '../services/mediaKitApi';
import MediaKitRenderer from '../components/mediaKit/MediaKitRenderer';
import AsinVideoPicker from '../components/mediaKit/AsinVideoPicker';
import ImageUploadField from '../components/mediaKit/ImageUploadField';
import PlatformIcon from '../components/mediaKit/PlatformIcon';
import LayoutPreviewCard from '../components/mediaKit/LayoutPreviewCard';

const EMPTY_KIT = {
    slug: '',
    display_name: '',
    tagline: '',
    avatar_url: '',
    location: '',
    bio: '',
    contact_email: '',
    show_contact_button: true,
    layout: 'aurora',
    theme: { primary: '#7c3aed', accent: '#06b6d4' },
    badges: [],
    socials: [],
    stats: [],
    specializations: { video_types: [], categories: [] },
    rate_card: [],
    is_published: false,
};

const LAYOUTS = [
    { id: 'aurora', label: 'Aurora', desc: 'Dark, glowing gradients' },
    { id: 'paperlight', label: 'Paper', desc: 'Clean, bright, minimal' },
    { id: 'editorial', label: 'Editorial', desc: 'Bold typographic, magazine-style' },
];

const PLATFORM_OPTIONS = ['amazon', 'youtube', 'tiktok', 'instagram', 'website', 'custom'];
const BADGE_OPTIONS = ['verified', 'top_creator', 'platinum', 'rising_star'];

// ── small reusable field ─────────────────────────────────────
function Field({ label, children, hint }) {
    return (
        <label className="block mb-4">
            <span className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</span>
            {children}
            {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
        </label>
    );
}
const inputCls = 'w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';

// ── section wrapper ──────────────────────────────────────────
function Card({ title, subtitle, children }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <h3 className="text-lg font-bold text-slate-800">{title}</h3>
            {subtitle && <p className="text-sm text-slate-400 mb-4">{subtitle}</p>}
            <div className={subtitle ? '' : 'mt-4'}>{children}</div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════
export default function MediaKitBuilderPage() {
    const { kitId } = useParams();
    const [kit, setKit] = useState(EMPTY_KIT);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saveMsg, setSaveMsg] = useState(null);
    const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'ok' | 'taken'
    const [showPreview, setShowPreview] = useState(false);
    const [showAsinPicker, setShowAsinPicker] = useState(false);
    const skipNextAutosave = useRef(true);

    useEffect(() => {
        mediaKitApi.getMine(kitId)
            .then(({ kit: k, videos: v }) => {
                if (k) setKit({ ...EMPTY_KIT, ...k });
                setVideos(v || []);
            })
            .finally(() => setLoading(false));
    }, [kitId]);

    // ── autosave: debounced, fires on any field change after initial load ──
    useEffect(() => {
        if (loading) return;
        if (skipNextAutosave.current) { skipNextAutosave.current = false; return; }
        const t = setTimeout(async () => {
            try {
                await mediaKitApi.update(kitId, kit);
                setSaveMsg({ type: 'ok', text: 'Saved' });
            } catch (e) {
                setSaveMsg({ type: 'error', text: e.message });
            } finally {
                setTimeout(() => setSaveMsg(null), 2000);
            }
        }, 900);
        return () => clearTimeout(t);
    }, [kit, kitId, loading]);

    const update = (patch) => setKit((k) => ({ ...k, ...patch }));

    // slug availability check (debounced)
    useEffect(() => {
        if (!kit.slug || kit.slug.length < 3) { setSlugStatus(null); return; }
        setSlugStatus('checking');
        const t = setTimeout(() => {
            mediaKitApi.checkSlug(kit.slug)
                .then((r) => setSlugStatus(r.available ? 'ok' : 'taken'))
                .catch(() => setSlugStatus(null));
        }, 500);
        return () => clearTimeout(t);
    }, [kit.slug]);

    // ── list editors (socials / stats / specializations / rate card) ──
    const addSocial = () => update({ socials: [...kit.socials, { platform: 'amazon', url: '', label: '' }] });
    const updateSocial = (i, patch) => update({ socials: kit.socials.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
    const removeSocial = (i) => update({ socials: kit.socials.filter((_, idx) => idx !== i) });

    const addStat = () => update({ stats: [...kit.stats, { label: '', value: '', verified: false }] });
    const updateStat = (i, patch) => update({ stats: kit.stats.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
    const removeStat = (i) => update({ stats: kit.stats.filter((_, idx) => idx !== i) });

    const addRate = () => update({ rate_card: [...kit.rate_card, { title: '', note: '', price: '', price_note: '' }] });
    const updateRate = (i, patch) => update({ rate_card: kit.rate_card.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
    const removeRate = (i) => update({ rate_card: kit.rate_card.filter((_, idx) => idx !== i) });

    const toggleBadge = (b) => {
        const has = kit.badges.includes(b);
        update({ badges: has ? kit.badges.filter((x) => x !== b) : [...kit.badges, b] });
    };

    const addTagTo = (field, value) => {
        if (!value.trim()) return;
        update({ specializations: { ...kit.specializations, [field]: [...(kit.specializations[field] || []), value.trim()] } });
    };
    const removeTagFrom = (field, idx) => {
        update({ specializations: { ...kit.specializations, [field]: kit.specializations[field].filter((_, i) => i !== idx) } });
    };

    // ── videos ──
    const refreshVideos = useCallback(() => {
        mediaKitApi.getMine(kitId).then(({ videos: v }) => setVideos(v || []));
    }, [kitId]);

    const addVideoFromAsinPicker = async (payload) => {
        await mediaKitApi.addVideo(kitId, payload);
        refreshVideos();
    };

    const removeVideo = async (id) => {
        await mediaKitApi.deleteVideo(kitId, id);
        setVideos((v) => v.filter((x) => x.id !== id));
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading your media kit…</div>;
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* top bar */}
            <div className="sticky top-0 z-[60] bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-3" style={{ isolation: 'isolate' }}>
                <div>
                    <h1 className="text-lg font-bold text-slate-800">Media Kit Builder</h1>
                    {kit.slug && (
                        <p className="text-xs text-slate-400 font-mono">multisellerpro.com/kit/{kit.slug}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {saveMsg && (
                        <span className={`text-xs font-semibold ${saveMsg.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>
                            {saveMsg.text}
                        </span>
                    )}
                    <button
                        onClick={() => setShowPreview((p) => !p)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
                    >
                        {showPreview ? <EyeOff size={15} /> : <Eye size={15} />} {showPreview ? 'Edit' : 'Preview'}
                    </button>
                    <span className="text-xs text-slate-400 hidden sm:inline">Autosaves as you type</span>
                    <button
                        onClick={() => update({ is_published: !kit.is_published })}
                        disabled={slugStatus === 'taken'}
                        className={`inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50 ${kit.is_published ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                        {kit.is_published ? 'Published — click to unpublish' : 'Publish'}
                    </button>
                    {kit.is_published && (
                        <a href={`/kit/${kit.slug}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 font-semibold">
                            View <ExternalLink size={14} />
                        </a>
                    )}
                </div>
            </div>

            {showPreview ? (
                <MediaKitRenderer kit={kit} videos={videos} isPreview />
            ) : (
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">

                    {/* URL / slug */}
                    <Card title="Your kit's link">
                        <Field label="Custom URL" hint="multisellerpro.com/kit/your-slug">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-400 shrink-0">/kit/</span>
                                <input
                                    className={inputCls}
                                    value={kit.slug}
                                    onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                                    placeholder="your-name"
                                />
                                {slugStatus === 'checking' && <Loader2 size={16} className="animate-spin text-slate-400" />}
                                {slugStatus === 'ok' && <Check size={16} className="text-green-600" />}
                                {slugStatus === 'taken' && <X size={16} className="text-red-500" />}
                            </div>
                            {slugStatus === 'taken' && <p className="text-xs text-red-500 mt-1">That URL is taken — try another.</p>}
                        </Field>
                    </Card>

                    {/* Profile */}
                    <Card title="Profile">
                        <Field label="Display name">
                            <input className={inputCls} value={kit.display_name} onChange={(e) => update({ display_name: e.target.value })} placeholder="Your Name / Brand" />
                        </Field>
                        <Field label="Tagline">
                            <input className={inputCls} value={kit.tagline} onChange={(e) => update({ tagline: e.target.value })} placeholder="e.g. Top Amazon Creator 🌟" />
                        </Field>
                        <ImageUploadField label="Avatar" value={kit.avatar_url} onChange={(url) => update({ avatar_url: url })} prefix="mediakit-avatar" />
                        <Field label="Location">
                            <input className={inputCls} value={kit.location} onChange={(e) => update({ location: e.target.value })} placeholder="City, State" />
                        </Field>
                        <Field label="Bio">
                            <textarea className={inputCls} rows={4} value={kit.bio} onChange={(e) => update({ bio: e.target.value })} placeholder="Tell brands about yourself, your niche, and what you love creating." />
                        </Field>
                        <Field label="Contact email">
                            <input className={inputCls} value={kit.contact_email} onChange={(e) => update({ contact_email: e.target.value })} placeholder="you@email.com" />
                        </Field>
                    </Card>

                    {/* Badges */}
                    <Card title="Badges" subtitle="Optional highlights shown next to your name">
                        <div className="flex flex-wrap gap-2">
                            {BADGE_OPTIONS.map((b) => (
                                <button
                                    key={b}
                                    onClick={() => toggleBadge(b)}
                                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${kit.badges.includes(b) ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                                        }`}
                                >
                                    {b.replace('_', ' ')}
                                </button>
                            ))}
                        </div>
                    </Card>

                    {/* Design */}
                    <Card title="Design" subtitle="Pick a layout and colors — updates your live preview instantly">
                        <div className="grid grid-cols-3 gap-3 mb-6">
                            {LAYOUTS.map((l) => (
                                <LayoutPreviewCard
                                    key={l.id}
                                    id={l.id}
                                    label={l.label}
                                    desc={l.desc}
                                    active={kit.layout === l.id}
                                    onClick={() => update({ layout: l.id })}
                                />
                            ))}
                        </div>

                        <div className="flex flex-wrap items-center gap-6 mb-5">
                            <div className="flex items-center gap-2">
                                <Palette size={16} className="text-slate-400" />
                                <label className="text-sm font-semibold text-slate-700">Primary</label>
                                <input type="color" value={kit.theme.primary} onChange={(e) => update({ theme: { ...kit.theme, primary: e.target.value } })} className="w-8 h-8 rounded cursor-pointer border border-slate-200" />
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-semibold text-slate-700">Accent</label>
                                <input type="color" value={kit.theme.accent} onChange={(e) => update({ theme: { ...kit.theme, accent: e.target.value } })} className="w-8 h-8 rounded cursor-pointer border border-slate-200" />
                            </div>
                            <button
                                onClick={() => {
                                    const rand = () => `#${Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0')}`;
                                    update({ theme: { ...kit.theme, primary: rand(), accent: rand() } });
                                }}
                                className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"
                            >
                                🎲 Randomize colors
                            </button>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-semibold text-slate-700 block mb-1.5">Corner style</label>
                                <div className="flex gap-2">
                                    {['lg', 'xl', '2xl'].map((r) => (
                                        <button key={r} onClick={() => update({ theme: { ...kit.theme, radius: r } })}
                                            className={`flex-1 text-xs font-semibold py-2 border transition ${kit.theme.radius === r || (!kit.theme.radius && r === 'xl') ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}
                                            style={{ borderRadius: r === 'lg' ? 8 : r === 'xl' ? 14 : 20 }}
                                        >
                                            {r === 'lg' ? 'Subtle' : r === 'xl' ? 'Rounded' : 'Very round'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-slate-700 block mb-1.5">Font pairing</label>
                                <div className="flex gap-2">
                                    {[{ id: 'sans', label: 'Sans' }, { id: 'serif', label: 'Serif' }, { id: 'mono', label: 'Mono' }].map((f) => (
                                        <button key={f.id} onClick={() => update({ theme: { ...kit.theme, font: f.id } })}
                                            className={`flex-1 text-xs font-semibold py-2 rounded-lg border transition ${kit.theme.font === f.id || (!kit.theme.font && f.id === 'sans') ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}
                                            style={{ fontFamily: f.id === 'serif' ? 'Georgia, serif' : f.id === 'mono' ? 'monospace' : 'inherit' }}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Socials */}
                    <Card title="Links & socials">
                        {kit.socials.map((s, i) => (
                            <div key={i} className="flex items-center gap-2 mb-3">
                                <div className="flex items-center gap-2 w-40 shrink-0">
                                    <PlatformIcon platform={s.platform} size={24} />
                                    <select className={inputCls} value={s.platform} onChange={(e) => updateSocial(i, { platform: e.target.value })}>
                                        {PLATFORM_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <input className={inputCls} placeholder="URL" value={s.url} onChange={(e) => updateSocial(i, { url: e.target.value })} />
                                <input className={inputCls + ' w-40'} placeholder="Label (optional)" value={s.label} onChange={(e) => updateSocial(i, { label: e.target.value })} />
                                <button onClick={() => removeSocial(i)} className="text-slate-400 hover:text-red-500 shrink-0"><Trash2 size={16} /></button>
                            </div>
                        ))}
                        <button onClick={addSocial} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600"><Plus size={15} /> Add link</button>
                    </Card>

                    {/* Stats */}
                    <Card title="Stats" subtitle="Numbers that make brands say yes">
                        {kit.stats.map((s, i) => (
                            <div key={i} className="flex items-center gap-2 mb-3">
                                <input className={inputCls} placeholder="Label (e.g. Items Sold)" value={s.label} onChange={(e) => updateStat(i, { label: e.target.value })} />
                                <input className={inputCls} placeholder="Value (e.g. 6,000+)" value={s.value} onChange={(e) => updateStat(i, { value: e.target.value })} />
                                <label className="flex items-center gap-1 text-xs text-slate-500 shrink-0">
                                    <input type="checkbox" checked={s.verified} onChange={(e) => updateStat(i, { verified: e.target.checked })} /> Verified
                                </label>
                                <button onClick={() => removeStat(i)} className="text-slate-400 hover:text-red-500 shrink-0"><Trash2 size={16} /></button>
                            </div>
                        ))}
                        <button onClick={addStat} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600"><Plus size={15} /> Add stat</button>
                    </Card>

                    {/* Specializations */}
                    <Card title="Specializations">
                        <TagEditor label="Video types" values={kit.specializations.video_types} onAdd={(v) => addTagTo('video_types', v)} onRemove={(i) => removeTagFrom('video_types', i)} />
                        <TagEditor label="Categories" values={kit.specializations.categories} onAdd={(v) => addTagTo('categories', v)} onRemove={(i) => removeTagFrom('categories', i)} />
                    </Card>

                    {/* Rate card */}
                    <Card title="Rate card">
                        {kit.rate_card.map((r, i) => (
                            <div key={i} className="border border-slate-200 rounded-lg p-3 mb-3 space-y-2">
                                <input className={inputCls} placeholder="Title (e.g. 60s Review Video)" value={r.title} onChange={(e) => updateRate(i, { title: e.target.value })} />
                                <input className={inputCls} placeholder="Note (what's included)" value={r.note} onChange={(e) => updateRate(i, { note: e.target.value })} />
                                <div className="flex gap-2">
                                    <input className={inputCls} placeholder="Price (e.g. $100)" value={r.price} onChange={(e) => updateRate(i, { price: e.target.value })} />
                                    <input className={inputCls} placeholder="Price note (optional)" value={r.price_note} onChange={(e) => updateRate(i, { price_note: e.target.value })} />
                                    <button onClick={() => removeRate(i)} className="text-slate-400 hover:text-red-500 shrink-0"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))}
                        <button onClick={addRate} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600"><Plus size={15} /> Add rate</button>
                    </Card>

                    {/* Videos */}
                    <Card title="Videos" subtitle="Paste any link — we'll fetch the thumbnail and player automatically">
                        <VideoUrlAdder onAdded={refreshVideos} kitId={kitId} />
                        <button
                            onClick={() => setShowAsinPicker(true)}
                            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 border border-slate-200 px-3 py-2 rounded-lg hover:bg-slate-50"
                        >
                            <Plus size={15} /> Or pick from your Amazon videos
                        </button>

                        {videos.length > 0 && (
                            <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                {videos.map((v) => (
                                    <div key={v.id} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2">
                                        <div className="w-16 h-10 rounded bg-slate-100 overflow-hidden shrink-0">
                                            {v.thumbnail_url && <img src={v.thumbnail_url} className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-semibold text-slate-700 truncate">{v.title || v.source_url}</p>
                                            <p className="text-[10px] uppercase text-slate-400 font-bold">{v.platform} · {v.section_label}</p>
                                        </div>
                                        <button onClick={() => removeVideo(v.id)} className="text-slate-400 hover:text-red-500 shrink-0"><Trash2 size={16} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                </div>
            )}

            {showAsinPicker && (
                <AsinVideoPicker
                    onClose={() => setShowAsinPicker(false)}
                    onAdd={addVideoFromAsinPicker}
                    alreadyAddedUrls={videos.map((v) => v.source_url)}
                />
            )}
        </div>
    );
}

// ── tag editor for specializations ────────────────────────────
function TagEditor({ label, values, onAdd, onRemove }) {
    const [draft, setDraft] = useState('');
    return (
        <div className="mb-4">
            <p className="text-sm font-semibold text-slate-700 mb-1.5">{label}</p>
            <div className="flex flex-wrap gap-2 mb-2">
                {values.map((v, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs font-medium bg-slate-100 px-2.5 py-1 rounded-full">
                        {v}
                        <button onClick={() => onRemove(i)}><X size={11} /></button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <input
                    className={inputCls}
                    value={draft}
                    placeholder={`Add ${label.toLowerCase()}…`}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { onAdd(draft); setDraft(''); } }}
                />
                <button
                    onClick={() => { onAdd(draft); setDraft(''); }}
                    className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold shrink-0"
                >
                    Add
                </button>
            </div>
        </div>
    );
}

// ── the "one-step URL" video adder ────────────────────────────
function VideoUrlAdder({ onAdded, kitId }) {
    const [url, setUrl] = useState('');
    const [sectionLabel, setSectionLabel] = useState('Videos');
    const [resolving, setResolving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);

    const handleResolve = async () => {
        if (!url.trim()) return;
        setResolving(true);
        setError(null);
        setPreview(null);
        try {
            const data = await mediaKitApi.resolveLink(url.trim());
            setPreview(data);
        } catch (e) {
            setError(e.message);
        } finally {
            setResolving(false);
        }
    };

    const confirmAdd = async () => {
        await mediaKitApi.addVideo(kitId, { ...preview, section_label: sectionLabel });
        setUrl(''); setPreview(null); setSectionLabel('Videos');
        onAdded();
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                    <LinkIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        className={inputCls + ' pl-9'}
                        placeholder="Paste a YouTube, TikTok, Amazon, or Instagram link…"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleResolve()}
                    />
                </div>
                <button
                    onClick={handleResolve}
                    disabled={resolving || !url.trim()}
                    className="inline-flex items-center gap-1.5 justify-center px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold disabled:opacity-50 shrink-0"
                >
                    {resolving ? <Loader2 size={15} className="animate-spin" /> : 'Fetch preview'}
                </button>
            </div>
            {error && <p className="text-xs text-red-500 mt-2">{error}</p>}

            {preview && (
                <div className="mt-4 flex items-center gap-3 border border-slate-200 rounded-lg p-3 bg-slate-50">
                    <div className="w-24 h-14 rounded bg-slate-200 overflow-hidden shrink-0">
                        {preview.thumbnail_url && <img src={preview.thumbnail_url} className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{preview.title || 'Untitled video'}</p>
                        <p className="text-[10px] uppercase font-bold text-slate-400">{preview.platform}</p>
                        <input
                            className="mt-1 w-full text-xs px-2 py-1 rounded border border-slate-200"
                            placeholder="Section label (e.g. YouTube, UGC)"
                            value={sectionLabel}
                            onChange={(e) => setSectionLabel(e.target.value)}
                        />
                    </div>
                    <button onClick={confirmAdd} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg shrink-0">
                        <Plus size={14} /> Add
                    </button>
                </div>
            )}
        </div>
    );
}